import React, { useMemo, useState, useEffect } from 'react';
import { Box, Heading, Center, Text, HStack, Badge, Spinner, VStack } from '@chakra-ui/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
// Maps + drilldown modules — register once against the shared Highcharts.
import HC_map from 'highcharts/modules/map';
import HC_drilldown from 'highcharts/modules/drilldown';
import type { BreakdownSlice } from '../../types/link';
import { CHART_FONT } from './chartTheme';

import { PRIMARY_BG, PRIMARY_BG_ACTIVE, PRIMARY_BG_HOVER, PRIMARY_SCALE } from '../../../../../theme/brand';
// Highcharts world choropleth of clicks by country, with click-to-drill-down
// into a country's regions (per the Highcharts maps drilldown pattern) and
// native pan/zoom. Matches the rest of the xNotify portal (Highcharts, Inter,
// no credits). The world topology + per-country topologies are fetched from
// the Highcharts mapdata CDN on demand (same as the official demo).
//
// World view: shaded by `data` (byCountry, ISO alpha-2). Drilled region view:
// shaded by `regionData` (byRegion, "CC|Region name") matched to the province
// names by a normalised string compare — best-effort (region naming varies);
// unmatched regions render unshaded.

(() => {
  const H = Highcharts as any;
  if (!H.__slMapInit) {
    if (typeof HC_map === 'function') (HC_map as any)(Highcharts);
    if (typeof HC_drilldown === 'function') (HC_drilldown as any)(Highcharts);
    H.__slMapInit = true;
  }
})();

const MAPDATA_BASE = 'https://code.highcharts.com/mapdata';
const WORLD_URL = `${MAPDATA_BASE}/custom/world.topo.json`;
const NULL_COLOR = '#f1f5f9'; // slate-100 — no clicks
const MIN_COLOR = PRIMARY_SCALE[100]; // indigo-100
const MAX_COLOR = PRIMARY_BG_ACTIVE; // indigo-700

// Crop the empty polar caps and drop the default padding so the world fills
// the card width edge-to-edge instead of floating small with dead space.
const WORLD_VIEW: any = {
  padding: 0,
  fitToGeometry: { type: 'MultiPoint', coordinates: [[-168, 74], [190, -56]] },
};

// White text + dark indigo outline → readable on both the lightest tint and
// the darkest country/region fills (fixes black-on-dark-purple).
const MAP_LABEL_STYLE = {
  fontSize: '10px',
  fontWeight: '700',
  color: '#ffffff',
  textOutline: `1.5px ${PRIMARY_SCALE[900]}`,
};

// Shorten the verbose world-map country names so labels don't sprawl.
const SHORT_NAMES: Record<string, string> = {
  'United States of America': 'USA',
  'United Kingdom': 'UK',
  'United Arab Emirates': 'UAE',
  'Russian Federation': 'Russia',
  'Republic of Korea': 'S. Korea',
  'Korea, Republic of': 'S. Korea',
  'Democratic Republic of the Congo': 'DR Congo',
  'Iran (Islamic Republic of)': 'Iran',
  'Syrian Arab Republic': 'Syria',
  'Tanzania, United Republic of': 'Tanzania',
  'Venezuela (Bolivarian Republic of)': 'Venezuela',
  'Bolivia (Plurinational State of)': 'Bolivia',
};

const norm = (s: string) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining diacritics
    .replace(/[^a-z0-9]/g, '');

interface Props {
  title?: string;
  data: BreakdownSlice[];
  /** Region rollup as "CC|Region name" (e.g. "PK|Punjab") for drilled views. */
  regionData?: BreakdownSlice[];
}

export const ClicksMapChart: React.FC<Props> = ({ title = 'Clicks by location', data, regionData = [] }) => {
  const [worldTopo, setWorldTopo] = useState<any | null>(null);
  const [loadErr, setLoadErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(WORLD_URL)
      .then((r) => {
        if (!r.ok) throw new Error('world map fetch failed');
        return r.json();
      })
      .then((topo) => !cancelled && setWorldTopo(topo))
      .catch(() => !cancelled && setLoadErr(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const total = useMemo(() => data.reduce((s, d) => s + d.value, 0), [data]);

  const options = useMemo<Highcharts.Options | null>(() => {
    if (!worldTopo) return null;
    const H = Highcharts as any;

    const byKey: Record<string, number> = {};
    for (const d of data) {
      const k = String(d.label || '').toLowerCase();
      if (k) byKey[k] = (byKey[k] || 0) + d.value;
    }
    const worldData = H.geojson(worldTopo);
    worldData.forEach((g: any) => {
      const k = g.properties['hc-key'];
      g.drilldown = k;
      g.value = byKey[k] ?? null;
    });

    // Region clicks for a given country code, keyed by normalised region name.
    const regionClicksFor = (cc: string) => {
      const out: Record<string, number> = {};
      for (const r of regionData) {
        const idx = String(r.label || '').indexOf('|');
        if (idx < 0) continue;
        const code = r.label.slice(0, idx).toUpperCase();
        if (code !== cc) continue;
        out[norm(r.label.slice(idx + 1))] = (out[norm(r.label.slice(idx + 1))] || 0) + r.value;
      }
      return out;
    };

    const onDrilldown = async function (this: any, e: any) {
      if (e.seriesOptions) return;
      const chart = this;
      const key = e.point.drilldown;
      const mapKey = `countries/${key}/${key}-all`;
      let fail: any = setTimeout(() => {
        chart.showLoading(`No detailed map for ${e.point.name}`);
        fail = setTimeout(() => chart.hideLoading(), 1400);
      }, 4000);
      chart.showLoading('Loading map…');
      try {
        const topology = await fetch(`${MAPDATA_BASE}/${mapKey}.topo.json`).then((r) => {
          if (!r.ok) throw new Error('no map');
          return r.json();
        });
        const regions = H.geojson(topology);
        const regionClicks = regionClicksFor(key.toUpperCase());
        regions.forEach((d: any) => {
          d.value = regionClicks[norm(d.properties && d.properties.name)] ?? null;
        });
        chart.mapView.update(
          Highcharts.merge(
            { insets: undefined, padding: 0 },
            (topology.objects.default && topology.objects.default['hc-recommended-mapview']) || {},
          ),
          false,
        );
        clearTimeout(fail);
        chart.hideLoading();
        chart.addSeriesAsDrilldown(e.point, {
          name: e.point.name,
          data: regions,
          nullColor: NULL_COLOR,
          borderColor: '#ffffff',
          borderWidth: 0.4,
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            format: '{point.name}',
            style: { ...MAP_LABEL_STYLE },
          },
        });
      } catch {
        clearTimeout(fail);
        chart.hideLoading();
        chart.showLoading(`No detailed map for ${e.point.name}`);
        setTimeout(() => chart.hideLoading(), 1400);
      }
    };

    const onDrillup = function (this: any, e: any) {
      const target = e.target;
      // restore the cropped world view (not fitToBounds, which re-adds the gap)
      setTimeout(() => {
        try {
          target.mapView.update(Highcharts.merge({ insets: undefined }, WORLD_VIEW), true);
        } catch {
          /* noop */
        }
      }, 0);
    };

    return {
      chart: {
        map: worldData,
        backgroundColor: 'transparent',
        style: { fontFamily: CHART_FONT },
        spacing: [6, 6, 6, 6],
        events: { drilldown: onDrilldown, drillup: onDrillup },
      },
      credits: { enabled: false },
      title: { text: undefined },
      mapView: WORLD_VIEW,
      mapNavigation: {
        enabled: true,
        enableMouseWheelZoom: false,
        buttonOptions: { verticalAlign: 'bottom', theme: { r: 4 } },
      },
      colorAxis: { min: 0, minColor: MIN_COLOR, maxColor: MAX_COLOR, nullColor: NULL_COLOR },
      legend: { align: 'center', verticalAlign: 'bottom', layout: 'horizontal' },
      tooltip: {
        useHTML: true,
        headerFormat: '',
        pointFormatter(this: any) {
          const v = this.value;
          return `<b>${this.name}</b><br/>${
            v == null ? 'No clicks' : `${Highcharts.numberFormat(v, 0)} click${v === 1 ? '' : 's'}`
          }`;
        },
      },
      plotOptions: { map: { states: { hover: { color: PRIMARY_SCALE[300] } } } },
      series: [
        {
          type: 'map',
          name: 'Clicks',
          data: worldData,
          nullColor: NULL_COLOR,
          borderColor: '#ffffff',
          borderWidth: 0.4,
          dataLabels: {
            enabled: true,
            allowOverlap: false,
            padding: 2,
            style: { ...MAP_LABEL_STYLE },
            formatter(this: any) {
              if (!this.point || this.point.value == null) return undefined;
              return SHORT_NAMES[this.point.name] || this.point.name;
            },
          },
        } as Highcharts.SeriesMapOptions,
      ],
      drilldown: {
        // Drillable points use THIS style for their labels — must stay readable
        // on dark fills (white + dark outline), not the default dark-blue links.
        activeDataLabelStyle: {
          color: '#ffffff',
          textDecoration: 'none',
          fontWeight: '700',
          textOutline: `1.5px ${PRIMARY_SCALE[900]}`,
        },
        breadcrumbs: { floating: true, buttonTheme: { style: { color: PRIMARY_BG_HOVER } } },
      },
    };
  }, [worldTopo, data, regionData]);

  return (
    <Box bg="white" borderWidth="1px" rounded="lg" p={4} shadow="xs">
      <HStack justify="space-between" mb={2} align="center">
        <Heading size="xs" color="gray.600">
          {title}
        </Heading>
        <Badge bg={PRIMARY_SCALE[50]} color={PRIMARY_SCALE[800]} fontSize="0.7rem" rounded="md" px={2}>
          {total.toLocaleString()} clicks
        </Badge>
      </HStack>
      {total === 0 ? (
        <Center h="320px">
          <Text color="gray.400" fontSize="sm">
            No location data yet
          </Text>
        </Center>
      ) : loadErr ? (
        <Center h="320px">
          <Text color="gray.400" fontSize="sm">
            Map could not be loaded
          </Text>
        </Center>
      ) : !options ? (
        <Center h="380px">
          <VStack spacing={3}>
            <Spinner color={PRIMARY_BG} />
            <Text color="gray.400" fontSize="xs">
              Loading map…
            </Text>
          </VStack>
        </Center>
      ) : (
        <Box h={{ base: '320px', md: '440px', xl: '480px' }}>
          <HighchartsReact
            highcharts={Highcharts}
            constructorType="mapChart"
            options={options}
            containerProps={{ style: { height: '100%', width: '100%' } }}
          />
        </Box>
      )}
      <Text mt={2} fontSize="xs" color="gray.400">
        Click a country to drill into its regions · drag to pan · use the map controls to zoom
      </Text>
    </Box>
  );
};
